/**
 * This file list differents utils functions.
 *
 *
 * -----
 */

export const identity = <T>(a: T): T => a;
export const deNull = <T>(a: T | null | undefined, defaultValue: T): T =>
  a === null || a === undefined ? defaultValue : a;

/**
 * Check for property availability.
 */
export function getOrElse<T>(
  value: T | null | undefined,
  format: (value: T) => string = String,
  defaultValue = '--'
): string {
  return value === null || value === undefined || value === ''
    ? defaultValue
    : format(value);
}

export function isFalsy(value: string | number): boolean {
  return value === 0 || value === "0";
}

export const getEntityById = <T extends { id: number }>(id: number, data: T[]) => {
  return data.find(dt => dt.id === id);
}

export const castExtension = (fileName: string) => {
  return fileName.replace(/\.[^/.]+$/, "");
}

export type RowKey<T extends readonly string[]> = T[number];
export type Row<T extends readonly string[]> = Record<RowKey<T>, string>;

export const sortRowsBy = <T extends readonly string[]>(key: RowKey<T>) => (a: Row<T>, b: Row<T>) => a[key].localeCompare(b[key], 'fr', { numeric: true });

/**
 * Check for form modifications.
 */
export function recordNotEqual<T>(prevue: T, actual: T): boolean {
  const isRecord = (val: unknown): val is Record<any, any> => {
    return val instanceof Object;
  };

  if (!isRecord(prevue) || !isRecord(actual)) {
    return false;
  }

  for (const key of Object.keys(prevue)) {
    if (Array.isArray(prevue[key])) {
      // check if is an array.
      if (!Array.isArray(actual[key])) {
        return true;
      }
      else {
        for (const [index, value] of prevue[key].entries()) {
          if ((actual[key] as number[])[index] !== value) {
            return true;
          }
        }
      }
    }
    if (prevue[key] !== actual[key]) {
      return true;
    }
  }
  return false;
}

/**
 * Transform a list (id, name) to a selection record.
 */
export function listToSelectionRecord(
  list: { id: number }[],
  trueValues: number[] = []
): Record<number, boolean> {
  const record = list.reduce(
    (acc, current) => ({
      ...acc,
      [current.id]: trueValues.includes(current.id),
    }),
    {} as Record<number, boolean>
  );
  return record;
}

/**
 * filter entites with string characters.
 */
export function filterEntitiesByText<T extends object>(entities: T[]) {
  return (substring: string): T[] => {
    return entities.filter((entity) =>
      Object.values(entity).some((value) =>
        String(value).toLowerCase().includes(substring.toLowerCase())
      )
    );
  };
}

/**
 * Multicritere filter.
 */
export function multiCriteriaFilter<
  T extends { id: number },
  U extends { id: number }
>(
  entitiesToFilter: T[],
  correcpondenceEntities: U[],
  entityKey: keyof U,
  correspondanceKey: keyof U
): (ids: number[], text: string) => T[] {
  const result = entitiesToFilter;

  return (ids: number[], text: string) => {
    const entityIds = new Set(
      correcpondenceEntities
        .filter((cor) => ids.includes(cor[correspondanceKey] as number))
        .map((entity) => entity[entityKey])
    );

    const filtred = result.filter(
      ({ id }) => entityIds.size === 0 || entityIds.has(id as U[keyof U])
    );

    return filterEntitiesByText(filtred)(text);
  };
}

export function filterBytext(value: string, values: string[]): string[] {
  return values.filter(str => str.includes(value));
}

/**
 * Wait for execute.
 */
export function wait<T>(
  durationMiliseconds: number,
  callback: () => T
): Promise<T> {
  return new Promise((resolve) =>
    setTimeout(() => resolve(callback()), durationMiliseconds)
  );
}

/**
 * Searching for a value.
 */
export const searchValue = <T extends { id: number }>(
  id: number,
  values: T[]
): T | null => {
  return values.find((value) => value.id === id) || null;
};

/**
 * Test if an object is empty.
 */
export function isEmptyObject(obj : object) {
    return Object.keys(obj).length === 0;
}

interface PipeSignature {
  <A, B>(a: A, ab: (a: A) => B): B;
  <A, B, C>(a: A, ab: (a: A) => B, bc: (b: B) => C): C;
  <A, B, C, D>(a: A, ab: (a: A) => B, bc: (b: B) => C, cd: (c: C) => D): D;
  <A, B, C, D, E>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E
  ): E;
  <A, B, C, D, E, F>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F
  ): F;
  <A, B, C, D, E, F, G>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G
  ): G;
  <A, B, C, D, E, F, G, H>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H
  ): H;
  <A, B, C, D, E, F, G, H, I>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I
  ): I;
  <A, B, C, D, E, F, G, H, I, J>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J
  ): J;
  <A, B, C, D, E, F, G, H, I, J, K>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K
  ): K;
  <A, B, C, D, E, F, G, H, I, J, K, L>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L
  ): L;
  <A, B, C, D, E, F, G, H, I, J, K, L, M>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M
  ): M;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N
  ): N;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O
  ): O;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P
  ): P;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q
  ): Q;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R
  ): R;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S
  ): S;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S,
    st: (s: S) => T
  ): T;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T, U>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S,
    st: (s: S) => T,
    tu: (t: T) => U
  ): U;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T, U, V>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S,
    st: (s: S) => T,
    tu: (t: T) => U,
    uv: (u: U) => V
  ): V;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T, U, V, W>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S,
    st: (s: S) => T,
    tu: (t: T) => U,
    uv: (u: U) => V,
    vw: (v: V) => W
  ): W;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T, U, V, W, X>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S,
    st: (s: S) => T,
    tu: (t: T) => U,
    uv: (u: U) => V,
    vw: (v: V) => W,
    wx: (w: W) => X
  ): X;
  <A, B, C, D, E, F, G, H, I, J, K, L, M, N, O, P, Q, R, S, T, U, V, W, X, Y>(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S,
    st: (s: S) => T,
    tu: (t: T) => U,
    uv: (u: U) => V,
    vw: (v: V) => W,
    wx: (w: W) => X,
    xy: (x: X) => Y
  ): Y;
  <
    A,
    B,
    C,
    D,
    E,
    F,
    G,
    H,
    I,
    J,
    K,
    L,
    M,
    N,
    O,
    P,
    Q,
    R,
    S,
    T,
    U,
    V,
    W,
    X,
    Y,
    Z
  >(
    a: A,
    ab: (a: A) => B,
    bc: (b: B) => C,
    cd: (c: C) => D,
    de: (d: D) => E,
    ef: (e: E) => F,
    fg: (f: F) => G,
    gh: (g: G) => H,
    hi: (h: H) => I,
    ij: (i: I) => J,
    jk: (j: J) => K,
    kl: (k: K) => L,
    lm: (l: L) => M,
    mn: (m: M) => N,
    no: (n: N) => O,
    op: (o: O) => P,
    pq: (p: P) => Q,
    qr: (q: Q) => R,
    rs: (r: R) => S,
    st: (s: S) => T,
    tu: (t: T) => U,
    uv: (u: U) => V,
    vw: (v: V) => W,
    wx: (w: W) => X,
    xy: (x: X) => Y,
    yz: (y: Y) => Z
  ): Z;
}

/**
 * Pipe function.
 */
export const pipe: PipeSignature = (a: any, ...fns: any[]) =>
  fns.reduce((acc, fn) => fn(acc), a);

interface CurrySignature {
  <A, B>(fn: (a: A) => B): (a: A) => B;
  <A, B, C>(fn: (a: A, b: B) => C): (a: A) => (b: B) => C;
  <A, B, C, D>(fn: (a: A, b: B, c: C) => D): (a: A) => (b: B) => (c: C) => D;
  <A, B, C, D, E>(fn: (a: A, b: B, c: C, d: D) => E): (
    a: A
  ) => (b: B) => (c: C) => (d: D) => E;
  <A, B, C, D, E, F>(fn: (a: A, b: B, c: C, d: D, e: E) => F): (
    a: A
  ) => (b: B) => (c: C) => (d: D) => (e: E) => F;
  <A, B, C, D, E, F, G>(fn: (a: A, b: B, c: C, d: D, e: E, f: F) => G): (
    a: A
  ) => (b: B) => (c: C) => (d: D) => (e: E) => (f: F) => G;
  <A, B, C, D, E, F, G, H>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G) => H
  ): (a: A) => (b: B) => (c: C) => (d: D) => (e: E) => (f: F) => (g: G) => H;
  <A, B, C, D, E, F, G, H, I>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H) => I
  ): (
    a: A
  ) => (b: B) => (c: C) => (d: D) => (e: E) => (f: F) => (g: G) => (h: H) => I;
  <A, B, C, D, E, F, G, H, I, J>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H, i: I) => J
  ): (
    a: A
  ) => (
    b: B
  ) => (c: C) => (d: D) => (e: E) => (f: F) => (g: G) => (h: H) => (i: I) => J;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H, i: I, j: J) => K
  ): (
    a: A
  ) => (
    b: B
  ) => (
    c: C
  ) => (d: D) => (e: E) => (f: F) => (g: G) => (h: H) => (i: I) => (j: J) => K;
}

/**
 * Curry function.
 */

export const curry: CurrySignature = (fn: any) => {
  switch (fn.length) {
    case 1:
      return fn;
    case 2:
      return (a: any) => (b: any) => fn(a, b);
    case 3:
      return (a: any) => (b: any) => (c: any) => fn(a, b, c);
    case 4:
      return (a: any) => (b: any) => (c: any) => (d: any) => fn(a, b, c, d);
    case 5:
      return (a: any) => (b: any) => (c: any) => (d: any) => (e: any) =>
        fn(a, b, c, d, e);
    case 6:
      return (a: any) =>
        (b: any) =>
          (c: any) =>
            (d: any) =>
              (e: any) =>
                (f: any) =>
                  fn(a, b, c, d, e, f);
    case 7:
      return (a: any) =>
        (b: any) =>
          (c: any) =>
            (d: any) =>
              (e: any) =>
                (f: any) =>
                  (g: any) =>
                    fn(a, b, c, d, e, f, g);
    case 8:
      return (a: any) =>
        (b: any) =>
          (c: any) =>
            (d: any) =>
              (e: any) =>
                (f: any) =>
                  (g: any) =>
                    (h: any) =>
                      fn(a, b, c, d, e, f, g, h);
    case 9:
      return (a: any) =>
        (b: any) =>
          (c: any) =>
            (d: any) =>
              (e: any) =>
                (f: any) =>
                  (g: any) =>
                    (h: any) =>
                      (i: any) =>
                        fn(a, b, c, d, e, f, g, h, i);
    case 10:
      return (a: any) =>
        (b: any) =>
          (c: any) =>
            (d: any) =>
              (e: any) =>
                (f: any) =>
                  (g: any) =>
                    (h: any) =>
                      (i: any) =>
                        (j: any) =>
                          fn(a, b, c, d, e, f, g, h, i, j);
    default:
      return fn;
  }
};

interface ReverseArgsSignature {
  <A, B, C>(fn: (a: A, b: B) => C): (b: B, a: A) => C;
  <A, B, C, D>(fn: (a: A, b: B, c: C) => D): (c: C, b: B, a: A) => D;
  <A, B, C, D, E>(fn: (a: A, b: B, c: C, d: D) => E): (
    d: D,
    c: C,
    b: B,
    a: A
  ) => E;
  <A, B, C, D, E, F>(fn: (a: A, b: B, c: C, d: D, e: E) => F): (
    e: E,
    d: D,
    c: C,
    b: B,
    a: A
  ) => F;
  <A, B, C, D, E, F, G>(fn: (a: A, b: B, c: C, d: D, e: E, f: F) => G): (
    f: F,
    e: E,
    d: D,
    c: C,
    b: B,
    a: A
  ) => G;
  <A, B, C, D, E, F, G, H>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G) => H
  ): (g: G, f: F, e: E, d: D, c: C, b: B, a: A) => H;
  <A, B, C, D, E, F, G, H, I>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H) => I
  ): (h: H, g: G, f: F, e: E, d: D, c: C, b: B, a: A) => I;
  <A, B, C, D, E, F, G, H, I, J>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H, i: I) => J
  ): (i: I, h: H, g: G, f: F, e: E, d: D, c: C, b: B, a: A) => J;
  <A, B, C, D, E, F, G, H, I, J, K>(
    fn: (a: A, b: B, c: C, d: D, e: E, f: F, g: G, h: H, i: I, j: J) => K
  ): (j: J, i: I, h: H, g: G, f: F, e: E, d: D, c: C, b: B, a: A) => K;
}

/**
 * Reverse arguments.
 */
export const reverseArgs: ReverseArgsSignature =
  (fn: any) =>
    (...args: any[]) =>
      fn(...args.reverse());

/**
 * Compare by id.
 */
export const includeById = <T extends { id: number }>(
  element: T,
  elements: T[]
): boolean => {
  return elements.some((e) => e.id === element.id);
};

/**
 * Compare by name.
 */
export const includeByName = <T extends { name: string }>(
  element: T,
  elements: T[]
): boolean => {
  return elements.some((e) => e.name === element.name);
};

/**
 * Generate a uuid
 */
export function uuidv4() {
  return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
    (
      +c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
    ).toString(16)
  );
}

/**
 * Get unique objects in an array by a specific key.
 */
export const removeRedundantByKey = <T, K extends keyof T>(arr: T[], key: K): T[] => {
  const seen = new Set<unknown>();
  return arr.filter(item => {
    if (seen.has(item[key])) return false;
    seen.add(item[key]);
    return true;
  });
};